/**
 * Parseo de fórmulas, agregados y evaluación numérica en totales.
 */
import { normalizeWidgetType, parseMetricSpec, serializeMetricSpec, widgetHasQueryShape } from './engineConfig';
import { formulaHasAggregates, extractAggCalls, formulaFieldRefs, replaceNthAggFn } from './formulaLanguage';

// Solo para recalcular totales en el navegador (evalNumericFormula): AGG(columna) → métrica auxiliar.
export const AGG_FN_RE = /\b(COUNT_DISTINCT|COUNTD|CONTEO_UNICO|MEDIAN|MEDIANA|SUM|SUMA|MAX|MAXIMO|MÁXIMO|MIN|MINIMO|MÍNIMO|MEAN|AVG|AVERAGE|PROMEDIO|COUNT|CONTEO|CUENTA)\s*\(\s*(?:`([^`]+)`|\[([^\]]+)\]|([^)]+?))\s*\)/gi;

const AGG_FN_MAP = {
    sum: 'sum', suma: 'sum',
    max: 'max', maximo: 'max', máximo: 'max',
    min: 'min', minimo: 'min', mínimo: 'min',
    mean: 'mean', avg: 'mean', average: 'mean', promedio: 'mean',
    count: 'count', conteo: 'count', cuenta: 'count',
    countd: 'count_distinct', count_distinct: 'count_distinct', conteo_unico: 'count_distinct',
    median: 'median', mediana: 'median'
};

// Detección de agregados y cambio de función: léxico compartido con el editor (ignora {FIXED …}).
export { formulaHasAggregates, extractAggCalls, replaceNthAggFn };

function normalizeAggFn(fn) {
    const key = String(fn || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return AGG_FN_MAP[key] || 'sum';
}

export function helperMetricName(agg, column) {
    let safe = String(column || '').replace(/[^\p{L}\p{N}_]+/gu, '_').replace(/^_+|_+$/g, '') || 'col';
    if (/^\d/.test(safe)) safe = `c_${safe}`;
    return `__agg_${agg}_${safe}`;
}

export function evalNumericFormula(formula, values = {}) {
    if (!formula) return null;
    let expr = String(formula);
    const valueKeys = Object.keys(values || {});
    expr = expr.replace(new RegExp(AGG_FN_RE.source, 'gi'), (full) => {
        const open = full.indexOf('(');
        const close = full.lastIndexOf(')');
        if (open < 0 || close <= open) return full;
        const inner = full.slice(open + 1, close).trim().replace(/^[`\[]+|[`\]]+$/g, '');
        const helper = helperMetricName(normalizeAggFn(full.slice(0, open)), inner);
        if (values[helper] != null) return helper;
        const colKey = valueKeys.find(key => key.toLowerCase() === inner.toLowerCase());
        return colKey || inner;
    });
    const names = Object.keys(values || {}).sort((a, b) => b.length - a.length);
    const tokens = {};
    names.forEach((name, i) => {
        const token = `__v${i}`;
        tokens[token] = values[name];
        const escaped = String(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        expr = expr.replace(new RegExp(escaped, 'g'), token);
    });
    const leftover = expr.replace(/__v\d+/g, '').replace(/[\s+\-*/().,0-9eE]/g, '');
    if (leftover) return null;
    try {
        const fn = new Function(...Object.keys(tokens), `"use strict"; return (${expr});`);
        const result = fn(...Object.values(tokens));
        if (typeof result !== 'number' || !Number.isFinite(result)) return null;
        return result;
    } catch (e) {
        return null;
    }
}

export function columnToField(col, dataSourceId, extra = {}) {
    const inferred = extra.inferred_type || col.inferred_type || 'string';
    const role = extra.role || (inferred === 'number' ? 'metric' : 'dimension');
    return {
        id: `col:${dataSourceId || ''}:${col.name}`,
        name: col.name,
        label: extra.label || col.label || col.name,
        kind: 'column',
        level: 'row',
        inferred_type: inferred,
        role,
        formula: null,
        dataSourceId
    };
}

/**
 * Campo de la paleta a partir de un campo calculado. Con allCalcs, lleva en `deps` los otros
 * calculados que usa su fórmula (transitorio: no se guarda, solo viaja al asignarlo a un widget).
 */
export function calculatedToField(field, allCalcs = []) {
    return {
        id: field.id,
        name: field.name,
        kind: 'calculated',
        level: field.level || 'row',
        inferred_type: field.inferred_type || 'number',
        role: field.level === 'agg' || field.inferred_type === 'number' ? 'metric' : 'dimension',
        formula: field.formula,
        defaultAgg: field.defaultAgg || ((field.inferred_type || 'number') === 'number' ? 'sum' : 'count'),
        aggregations: field.aggregations || {},
        deps: allCalcs.length ? calcDependencies(field, allCalcs) : []
    };
}

/**
 * Campos calculados que usa la fórmula de `field` (y los que usan esos), en orden de cálculo:
 * cada dependencia va antes de quien la usa. Los ciclos se cortan (el motor los reporta).
 */
export function calcDependencies(field, allCalcs = []) {
    const byName = new Map(allCalcs.map(item => [item.name, item]));
    const names = [...byName.keys()];
    const ordered = [];
    const seen = new Set([field.name]);
    const visit = (current) => {
        formulaFieldRefs(current.formula || '', names).forEach(refName => {
            if (seen.has(refName)) return;
            seen.add(refName);
            const dep = byName.get(refName);
            visit(dep);
            ordered.push({ name: dep.name, formula: dep.formula, level: dep.level || 'row', aggregations: dep.aggregations || {} });
        });
    };
    visit(field);
    return ordered;
}

/** Agrega a la consulta las dependencias que falten, antes de los campos que ya tenía. */
function ensureCalcDependencies(query, deps = []) {
    if (!deps || !deps.length) return query;
    const rowList = query.row_calculated_fields || [];
    const aggList = query.agg_calculated_fields || [];
    const has = (list, name) => list.some(item => item.name === name);
    const missingRow = deps.filter(dep => dep.level !== 'agg' && !has(rowList, dep.name))
        .map(dep => ({ name: dep.name, formula: dep.formula }));
    const missingAgg = deps.filter(dep => dep.level === 'agg' && !has(aggList, dep.name))
        .map(dep => ({ name: dep.name, formula: dep.formula, aggregations: dep.aggregations || {} }));
    if (!missingRow.length && !missingAgg.length) return query;
    return {
        ...query,
        row_calculated_fields: [...missingRow, ...rowList],
        agg_calculated_fields: [...missingAgg, ...aggList]
    };
}

export function extractFormulaRefs(formula, knownNames) {
    if (!formula) return [];
    return knownNames.filter(name => {
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`(^|[^\\w\`])${escaped}([^\\w\`]|$)`).test(formula);
    });
}

function ensureRowCalc(query, field) {
    if (field.kind !== 'calculated' || field.level !== 'row' || !field.formula) return query;
    const list = query.row_calculated_fields || [];
    const entry = { name: field.name, formula: field.formula };
    const idx = list.findIndex(item => item.name === field.name);
    if (idx >= 0) {
        const next = [...list];
        next[idx] = entry;
        return ensureCalcDependencies({ ...query, row_calculated_fields: next }, field.deps);
    }
    return ensureCalcDependencies({
        ...query,
        row_calculated_fields: [...list, entry]
    }, field.deps);
}

function ensureAggCalc(query, field) {
    if (field.kind !== 'calculated' || field.level !== 'agg' || !field.formula) return query;
    const list = query.agg_calculated_fields || [];
    const entry = { name: field.name, formula: field.formula, aggregations: field.aggregations || {} };
    const idx = list.findIndex(item => item.name === field.name);
    if (idx >= 0) {
        const next = [...list];
        next[idx] = { ...list[idx], ...entry };
        return ensureCalcDependencies({ ...query, agg_calculated_fields: next }, field.deps);
    }
    return ensureCalcDependencies({
        ...query,
        agg_calculated_fields: [...list, entry]
    }, field.deps);
}

/**
 * Métricas que el widget muestra, en orden. Una lista guardada vacía (widgets viejos) significa
 * «todas las métricas más los calculados agregados»; los nombres que ya no existen se descartan.
 */
export function visibleMetricList(query = {}) {
    const metrics = query.metrics || {};
    const aggNames = (query.agg_calculated_fields || []).map(item => item.name);
    const saved = query.visibleMetrics || [];
    const base = saved.length ? saved : [...Object.keys(metrics), ...aggNames];
    return base.filter((name, idx, list) => list.indexOf(name) === idx && (metrics[name] || aggNames.includes(name)));
}

function addVisible(query, name) {
    /* Se parte de la lista efectiva: con visibleMetrics vacío, agregar una métrica no debe ocultar las demás. */
    const visible = visibleMetricList(query);
    if (visible.includes(name)) return { ...query, visibleMetrics: visible };
    return { ...query, visibleMetrics: [...visible, name] };
}

/** Columnas que citan los calculados agregados sin SUM(…) (p. ej. Clics/Impresiones): necesitan su métrica. */
function aggFormulaRefs(query, names) {
    const refs = new Set();
    (query.agg_calculated_fields || []).forEach((item) => {
        if (extractAggCalls(item.formula).length) return;
        extractFormulaRefs(item.formula, names).forEach(ref => refs.add(ref));
    });
    return refs;
}

function cloneQuery(query = {}) {
    return {
        ...query,
        dimensions: [...(query.dimensions || [])],
        metrics: { ...(query.metrics || {}) },
        row_calculated_fields: [...(query.row_calculated_fields || [])],
        agg_calculated_fields: [...(query.agg_calculated_fields || [])],
        visibleMetrics: [...(query.visibleMetrics || [])],
        aliases: { ...(query.aliases || {}) },
        metricFormats: { ...(query.metricFormats || {}) },
        totalAggs: { ...(query.totalAggs || {}) },
        totalFormulas: { ...(query.totalFormulas || {}) },
        conditionalFormats: { ...(query.conditionalFormats || {}) },
        fixedFilters: [...(query.fixedFilters || [])],
    };
}

function applyVisibleLabel(query, field) {
    const visibleLabel = String(field?.label || '').trim();
    if (visibleLabel && visibleLabel !== field.name) {
        query.aliases = { ...query.aliases, [field.name]: visibleLabel };
    }
    return visibleLabel;
}

/** Al soltar un campo de otra fuente (o al sustituir el KPI) se limpia la consulta previa. */
function resetQueryForIncomingField(query, field) {
    const sourceChanged = Boolean(field.dataSourceId && field.dataSourceId !== query.dataSourceId);
    return {
        ...query,
        dataSourceId: field.dataSourceId || query.dataSourceId,
        spreadsheet_id: sourceChanged ? undefined : query.spreadsheet_id,
        sheet_name: sourceChanged ? undefined : query.sheet_name,
        dimensions: [],
        metrics: {},
        visibleMetrics: [],
        row_calculated_fields: [],
        agg_calculated_fields: [],
        aliases: {},
        metricFormats: {},
        totalAggs: {},
        totalFormulas: {},
        conditionalFormats: {},
        fixedFilters: sourceChanged ? [] : [...(query.fixedFilters || [])],
    };
}

function kpiMetricNames(query = {}) {
    const visible = query.visibleMetrics || [];
    if (visible.length) return visible;
    return Object.keys(query.metrics || {});
}

/** Arrastra un campo (columna o calculado) al widget: dimensión, métrica o filtro. */
export function applyFieldToWidget(widget, field, slot = 'auto', knownNames = []) {
    if (!widget || !field?.name) return widget;
    const type = normalizeWidgetType(widget.type);
    let query = cloneQuery(widget.query);

    if (slot === 'filter') {
        return addFixedFilter(widget, field);
    }

    const visibleLabel = String(field.label || '').trim();

    if (type === 'slicer') {
        if (field.dataSourceId && field.dataSourceId !== query.dataSourceId) {
            query = resetQueryForIncomingField(query, field);
        } else if (field.dataSourceId) {
            query.dataSourceId = field.dataSourceId;
        }
        query.dimensions = [field.name];
        query.metrics = {};
        query.visibleMetrics = [];
        query = ensureRowCalc(query, field);
        applyVisibleLabel(query, field);
        return { ...widget, query, title: widget.title === 'Filtro' ? (visibleLabel || field.name) : widget.title };
    }

    const autoLevel = field.level === 'agg' || formulaHasAggregates(field.formula);
    const autoSlot = field.role === 'metric' || field.inferred_type === 'number' || autoLevel
        ? 'metric'
        : 'dimension';
    let target = slot === 'auto' ? autoSlot : slot;
    if (type === 'kpi') target = 'metric';

    if (type === 'kpi' && target === 'metric') {
        query = resetQueryForIncomingField(query, field);
    } else if (field.dataSourceId && query.dataSourceId && field.dataSourceId !== query.dataSourceId && widgetHasQueryShape(query)) {
        /* Un elemento con datos no se reinicia por un campo de otra fuente: se cambia de fuente
           desde «Reemplazar fuente» (modo elemento) para no perder su configuración. */
        return widget;
    } else if (field.dataSourceId && field.dataSourceId !== query.dataSourceId) {
        query = resetQueryForIncomingField(query, field);
    } else if (field.dataSourceId && !query.dataSourceId) {
        query.dataSourceId = field.dataSourceId;
    }

    applyVisibleLabel(query, field);

    if (formulaHasAggregates(field.formula)) {
        field = { ...field, level: 'agg' };
    }
    if (field.level !== 'agg') {
        query = ensureRowCalc(query, field);
    }

    if (target === 'dimension') {
        if (field.level === 'agg') return widget;
        if (!query.dimensions.includes(field.name)) {
            query.dimensions = [...query.dimensions, field.name];
        }
        delete query.metrics[field.name];
        query.visibleMetrics = query.visibleMetrics.filter(name => name !== field.name);
        return { ...widget, query };
    }

    if (field.level === 'agg') {
        query = ensureAggCalc(query, field);
        const aggCalls = extractAggCalls(field.formula);
        if (aggCalls.length === 0) {
            const refs = extractFormulaRefs(field.formula, knownNames);
            const aggs = field.aggregations || {};
            refs.forEach(ref => {
                if (!query.metrics[ref] && !query.dimensions.includes(ref)) {
                    query.metrics[ref] = aggs[ref] || 'sum';
                }
            });
        }
        query = addVisible(query, field.name);
        if (!query.totalAggs[field.name]) query.totalAggs[field.name] = 'formula';
        return finishKpiWidget(widget, query, field, type, visibleLabel);
    }

    const defaultAgg = field.defaultAgg || (field.inferred_type === 'number' ? 'sum' : 'count');
    query.metrics = { ...query.metrics, [field.name]: query.metrics[field.name] || defaultAgg };
    query.dimensions = query.dimensions.filter(name => name !== field.name);
    query = addVisible(query, field.name);
    return finishKpiWidget(widget, query, field, type, visibleLabel);
}

function finishKpiWidget(widget, query, field, type, visibleLabel) {
    if (type !== 'kpi') return { ...widget, query };
    const previousNames = kpiMetricNames(widget.query);
    const previousTitle = previousNames[0];
    const nextLabel = visibleLabel || field.name;
    const title = !widget.title || widget.title === 'KPI' || widget.title === previousTitle
        ? nextLabel
        : widget.title;
    return { ...widget, query, title };
}

export function removeFieldFromWidget(widget, name, slot) {
    if (!widget) return widget;
    const query = {
        ...(widget.query || {}),
        dimensions: [...(widget.query?.dimensions || [])],
        metrics: { ...(widget.query?.metrics || {}) },
        visibleMetrics: [...(widget.query?.visibleMetrics || [])],
        row_calculated_fields: [...(widget.query?.row_calculated_fields || [])],
        agg_calculated_fields: [...(widget.query?.agg_calculated_fields || [])],
        aliases: { ...(widget.query?.aliases || {}) },
        metricFormats: { ...(widget.query?.metricFormats || {}) },
        totalAggs: { ...(widget.query?.totalAggs || {}) },
        totalFormulas: { ...(widget.query?.totalFormulas || {}) },
        conditionalFormats: { ...(widget.query?.conditionalFormats || {}) },
        fixedFilters: [...(widget.query?.fixedFilters || [])]
    };

    if (slot === 'dimension') {
        query.dimensions = query.dimensions.filter(item => item !== name);
    } else {
        const visible = visibleMetricList(query).filter(item => item !== name);
        query.visibleMetrics = visible;
        query.agg_calculated_fields = query.agg_calculated_fields.filter(item => item.name !== name);
        /* Las métricas auxiliares de un calculado (ocultas) se van con él; las que aún cita otro calculado se quedan. */
        const stillUsed = aggFormulaRefs(query, Object.keys(query.metrics));
        Object.keys(query.metrics).forEach((key) => {
            if (!visible.includes(key) && !stillUsed.has(key)) delete query.metrics[key];
        });
        delete query.metricFormats[name];
        delete query.totalAggs[name];
        delete query.totalFormulas[name];
        delete query.conditionalFormats[name];
    }
    delete query.aliases[name];
    return { ...widget, query };
}

export function addFixedFilter(widget, field) {
    if (!widget || !field?.name) return widget;
    const isMetric = field.role === 'metric' || field.inferred_type === 'number' || field.level === 'agg';
    const next = {
        id: `ff-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
        column: field.name,
        role: isMetric ? 'metric' : 'dimension',
        inferred_type: field.inferred_type || (isMetric ? 'number' : 'string'),
        operator: isMetric ? 'gte' : (field.inferred_type === 'date' ? 'between' : 'eq'),
        value: ''
    };
    let query = cloneQuery(widget.query);
    if (field.dataSourceId && field.dataSourceId !== query.dataSourceId) {
        query = resetQueryForIncomingField(query, field);
    } else if (field.dataSourceId && !query.dataSourceId) {
        query.dataSourceId = field.dataSourceId;
    }
    query = ensureRowCalc(query, field);
    query.fixedFilters = [...(query.fixedFilters || []), next];
    return { ...widget, query };
}

export function updateFixedFilter(widget, filterId, patch) {
    if (!widget) return widget;
    const list = (widget.query?.fixedFilters || []).map(item => (
        item.id === filterId ? { ...item, ...patch } : item
    ));
    return { ...widget, query: { ...widget.query, fixedFilters: list } };
}

export function removeFixedFilter(widget, filterId) {
    if (!widget) return widget;
    return {
        ...widget,
        query: {
            ...widget.query,
            fixedFilters: (widget.query?.fixedFilters || []).filter(item => item.id !== filterId)
        }
    };
}

export function setFieldAlias(widget, name, alias) {
    if (!widget) return widget;
    const aliases = { ...(widget.query?.aliases || {}) };
    const trimmed = (alias || '').trim();
    if (!trimmed || trimmed === name) delete aliases[name];
    else aliases[name] = trimmed;
    return { ...widget, query: { ...widget.query, aliases } };
}

export function setMetricAgg(widget, name, agg) {
    return setMetricSpec(widget, name, { agg });
}

export function setMetricSpec(widget, name, patch) {
    if (!widget || !name) return widget;
    const current = parseMetricSpec(widget.query?.metrics?.[name]);
    const next = serializeMetricSpec({ ...current, ...patch });
    return {
        ...widget,
        query: {
            ...widget.query,
            metrics: { ...(widget.query?.metrics || {}), [name]: next }
        }
    };
}

export function setMetricFormat(widget, name, format) {
    if (!widget) return widget;
    const metricFormats = { ...(widget.query?.metricFormats || {}) };
    const prev = metricFormats[name];
    const decimals = prev && typeof prev === 'object' ? prev.decimals : null;
    if ((!format || format === 'inherit') && (decimals == null || decimals === '')) {
        delete metricFormats[name];
    } else if (!format || format === 'inherit') {
        metricFormats[name] = { type: 'inherit', decimals };
    } else if (decimals == null || decimals === '') {
        metricFormats[name] = format;
    } else {
        metricFormats[name] = { type: format, decimals };
    }
    return { ...widget, query: { ...widget.query, metricFormats } };
}

export function setMetricDecimals(widget, name, decimals) {
    if (!widget) return widget;
    const metricFormats = { ...(widget.query?.metricFormats || {}) };
    const prev = metricFormats[name];
    const type = !prev ? 'inherit' : (typeof prev === 'object' ? (prev.type || 'inherit') : prev);
    const nextDecimals = decimals === '' || decimals == null || Number.isNaN(Number(decimals))
        ? null
        : Math.max(0, Math.min(8, Number(decimals)));
    if (type === 'inherit' && nextDecimals == null) {
        delete metricFormats[name];
    } else if (nextDecimals == null) {
        metricFormats[name] = type;
    } else {
        metricFormats[name] = { type, decimals: nextDecimals };
    }
    return { ...widget, query: { ...widget.query, metricFormats } };
}

export function setMetricTotalAgg(widget, name, agg) {
    if (!widget) return widget;
    const totalAggs = { ...(widget.query?.totalAggs || {}) };
    if (!agg || agg === 'auto') delete totalAggs[name];
    else totalAggs[name] = agg;
    return { ...widget, query: { ...widget.query, totalAggs } };
}

export const CONDITION_OPS = [
    { value: 'gt', label: 'Mayor que' },
    { value: 'gte', label: 'Mayor o igual' },
    { value: 'lt', label: 'Menor que' },
    { value: 'lte', label: 'Menor o igual' },
    { value: 'eq', label: 'Igual a' },
    { value: 'neq', label: 'Distinto de' },
    { value: 'between', label: 'Entre' },
    { value: 'empty', label: 'Vacío' },
    { value: 'not_empty', label: 'No vacío' }
];

export function createConditionalRule() {
    return {
        id: `cf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`,
        op: 'gt',
        value: 0,
        valueTo: '',
        color: '#137333',
        background: '#e6f4ea',
        bold: false
    };
}

export function matchCondition(rule, raw) {
    const op = rule?.op || 'gt';
    if (op === 'empty') return raw == null || raw === '' || (typeof raw === 'number' && Number.isNaN(raw));
    if (op === 'not_empty') return raw != null && raw !== '' && !(typeof raw === 'number' && Number.isNaN(raw));
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return false;
    const a = Number(rule.value);
    const b = Number(rule.valueTo);
    if (op === 'gt') return raw > a;
    if (op === 'gte') return raw >= a;
    if (op === 'lt') return raw < a;
    if (op === 'lte') return raw <= a;
    if (op === 'eq') return raw === a;
    if (op === 'neq') return raw !== a;
    if (op === 'between' && Number.isFinite(a) && Number.isFinite(b)) {
        return raw >= Math.min(a, b) && raw <= Math.max(a, b);
    }
    return false;
}

export function firstConditionalStyle(rules, raw) {
    const list = Array.isArray(rules) ? rules : [];
    const hit = list.find(rule => matchCondition(rule, raw));
    if (!hit) return {};
    return {
        ...(hit.color ? { color: hit.color } : {}),
        ...(hit.background ? { background: hit.background } : {}),
        ...(hit.bold ? { fontWeight: 700 } : {})
    };
}

export function setMetricConditionalFormats(widget, name, rules) {
    if (!widget || !name) return widget;
    const conditionalFormats = { ...(widget.query?.conditionalFormats || {}) };
    if (!rules || rules.length === 0) delete conditionalFormats[name];
    else conditionalFormats[name] = rules;
    return { ...widget, query: { ...widget.query, conditionalFormats } };
}

export function setMetricTotalFormula(widget, name, formula) {
    if (!widget) return widget;
    const totalFormulas = { ...(widget.query?.totalFormulas || {}) };
    const trimmed = String(formula || '').trim();
    if (!trimmed) delete totalFormulas[name];
    else totalFormulas[name] = trimmed;
    const totalAggs = { ...(widget.query?.totalAggs || {}), [name]: 'formula' };
    return { ...widget, query: { ...widget.query, totalFormulas, totalAggs } };
}

export function patchAggCalculatedField(widget, name, patch) {
    if (!widget || !name) return widget;
    return {
        ...widget,
        query: {
            ...widget.query,
            agg_calculated_fields: (widget.query?.agg_calculated_fields || []).map(item => (
                item.name === name ? { ...item, ...patch } : item
            ))
        }
    };
}

function remapName(value, from, to) {
    return value === from ? to : value;
}

function remapKeyMap(map, from, to) {
    if (!map || from === to || !(from in map)) return { ...(map || {}) };
    const next = { ...map };
    next[to] = next[from];
    delete next[from];
    return next;
}

/**
 * Aplica la edición de un campo calculado (fórmula, nombre, nivel) a los widgets que lo usan.
 * allCalcs (la lista ya actualizada) permite sumar los calculados nuevos que cite la fórmula.
 */
export function updateCalculatedInWidgets(widgets, prevField, nextField, allCalcs = []) {
    const from = prevField?.name;
    const to = nextField?.name;
    if (!from || !to) return widgets;
    const deps = allCalcs.length ? calcDependencies(nextField, allCalcs) : [];

    const apply = (widget) => {
        const query = { ...(widget.query || {}) };
        let rowList = [...(query.row_calculated_fields || [])];
        let aggList = [...(query.agg_calculated_fields || [])];
        const entry = {
            name: to,
            formula: nextField.formula,
            aggregations: nextField.aggregations || {}
        };

        rowList = rowList.filter(item => item.name !== from);
        aggList = aggList.filter(item => item.name !== from);
        const wasUsed = (query.row_calculated_fields || []).some(item => item.name === from)
            || (query.agg_calculated_fields || []).some(item => item.name === from)
            || (query.visibleMetrics || []).includes(from)
            || (query.dimensions || []).includes(from)
            || Boolean(query.metrics?.[from]);

        if (wasUsed) {
            if (nextField.level === 'agg') aggList = [...aggList, entry];
            else rowList = [...rowList, { name: to, formula: nextField.formula }];
        }

        query.row_calculated_fields = rowList;
        query.agg_calculated_fields = aggList;
        if (wasUsed) Object.assign(query, ensureCalcDependencies(query, deps));
        query.dimensions = (query.dimensions || []).map(name => remapName(name, from, to));
        query.visibleMetrics = (query.visibleMetrics || []).map(name => remapName(name, from, to));
        query.metrics = remapKeyMap(query.metrics, from, to);
        if (nextField.level === 'agg' && nextField.aggregations) {
            Object.entries(nextField.aggregations).forEach(([ref, agg]) => {
                if (ref !== to && query.metrics[ref] != null && typeof query.metrics[ref] === 'string') {
                    query.metrics[ref] = agg;
                }
            });
        }
        query.aliases = remapKeyMap(query.aliases, from, to);
        query.metricFormats = remapKeyMap(query.metricFormats, from, to);
        query.totalAggs = remapKeyMap(query.totalAggs, from, to);
        query.totalFormulas = remapKeyMap(query.totalFormulas, from, to);
        query.conditionalFormats = remapKeyMap(query.conditionalFormats, from, to);
        const next = { ...widget, query };
        if (next.children?.widgets) {
            next.children = {
                ...next.children,
                widgets: updateCalculatedInWidgets(next.children.widgets, prevField, nextField, allCalcs)
            };
        }
        return next;
    };

    return (widgets || []).map(apply);
}

export function removeCalculatedFromWidgets(widgets, calcField) {
    return widgets.map(widget => {
        const query = { ...(widget.query || {}) };
        if (calcField.level === 'row') {
            query.row_calculated_fields = (query.row_calculated_fields || []).filter(item => item.name !== calcField.name);
        } else {
            query.agg_calculated_fields = (query.agg_calculated_fields || []).filter(item => item.name !== calcField.name);
        }
        query.dimensions = (query.dimensions || []).filter(name => name !== calcField.name);
        const metrics = { ...(query.metrics || {}) };
        delete metrics[calcField.name];
        query.metrics = metrics;
        query.visibleMetrics = (query.visibleMetrics || []).filter(name => name !== calcField.name);
        const aliases = { ...(query.aliases || {}) };
        delete aliases[calcField.name];
        query.aliases = aliases;
        const metricFormats = { ...(query.metricFormats || {}) };
        delete metricFormats[calcField.name];
        query.metricFormats = metricFormats;
        const totalAggs = { ...(query.totalAggs || {}) };
        delete totalAggs[calcField.name];
        query.totalAggs = totalAggs;
        const totalFormulas = { ...(query.totalFormulas || {}) };
        delete totalFormulas[calcField.name];
        query.totalFormulas = totalFormulas;
        const conditionalFormats = { ...(query.conditionalFormats || {}) };
        delete conditionalFormats[calcField.name];
        query.conditionalFormats = conditionalFormats;
        const next = { ...widget, query };
        if (next.children?.widgets) {
            next.children = {
                ...next.children,
                widgets: removeCalculatedFromWidgets(next.children.widgets, calcField)
            };
        }
        return next;
    });
}

export function preventDragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
}

export function reorderList(list, fromIndex, toIndex) {
    const next = [...(list || [])];
    if (fromIndex < 0 || fromIndex >= next.length) return list;
    const clamped = Math.max(0, Math.min(next.length - 1, Number(toIndex)));
    const [item] = next.splice(fromIndex, 1);
    next.splice(clamped, 0, item);
    return next;
}

export function reorderDimensions(widget, fromIndex, toIndex) {
    if (!widget) return widget;
    return {
        ...widget,
        query: {
            ...widget.query,
            dimensions: reorderList(widget.query?.dimensions || [], fromIndex, toIndex)
        }
    };
}

export function reorderVisibleMetrics(widget, fromIndex, toIndex) {
    if (!widget) return widget;
    /* Mismos índices que la repisa de métricas del configurador (visibleMetricList). */
    const visible = visibleMetricList(widget.query);
    return {
        ...widget,
        query: {
            ...widget.query,
            visibleMetrics: reorderList(visible, fromIndex, toIndex)
        }
    };
}
